import { FC } from 'react';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useMeetingPrepBriefsSuspenseQuery } from '../../../../../../queries/MeetingPrep/useMeetingPrepBriefsQuery';
import {
	selectBoundMeetingPrepTemplate,
	useMeetingPrepTemplatesQuery,
	useMeetingPrepTemplatesSuspenseQuery,
} from '../../../../../../queries/MeetingPrep/useMeetingPrepTemplates';
import { getScheduleTimezone } from '../../../../settings/PersonalSettings/EmailExecAssistantSettings/scheduleUtils';
import { DashboardMeetingPrepFirstRunRow } from './DashboardMeetingPrepFirstRunRow';
import { DashboardMeetingPrepLiveRow } from './DashboardMeetingPrepLiveRow';
import { DashboardMeetingPrepPausedRow } from './DashboardMeetingPrepPausedRow';
import { DashboardMeetingPrepQuietRow } from './DashboardMeetingPrepQuietRow';
import { DashboardMeetingPrepTodayDoneRow } from './DashboardMeetingPrepTodayDoneRow';
import { hasEverDelivered, nextUpcomingBrief, todayPreppedDay } from './helpers';

export const MEETING_PREP_TELEMETRY_CATEGORY = 'DashboardMeetingPrepRow';

export const useMeetingPrepRowVisibility = (): { visible: boolean } => {
	const userSession = useUserSession();
	const enabled = !!userSession.account?.features?.meetingPrep?.enabled;
	const templatesQuery = useMeetingPrepTemplatesQuery({ enabled });
	if (!enabled || templatesQuery.isError) {
		return { visible: false };
	}
	return { visible: templatesQuery.isPending || !!selectBoundMeetingPrepTemplate(templatesQuery.data ?? []) };
};

export const DashboardMeetingPrepRow: FC = () => {
	const userSession = useUserSession();
	const timeZone = getScheduleTimezone(userSession.user);
	const templatesQuery = useMeetingPrepTemplatesSuspenseQuery({
		meta: { errorLogging: { category: MEETING_PREP_TELEMETRY_CATEGORY, action: 'LoadTemplates-Error' } },
	});
	const briefsQuery = useMeetingPrepBriefsSuspenseQuery({
		meta: { errorLogging: { category: MEETING_PREP_TELEMETRY_CATEGORY, action: 'LoadBriefs-Error' } },
	});

	const template = selectBoundMeetingPrepTemplate(templatesQuery.data ?? []);
	if (!template) {
		return null;
	}

	const briefs = briefsQuery.data.pages.flatMap(page => page.values ?? []);
	const nextBrief = nextUpcomingBrief(briefs);
	const todayDay = todayPreppedDay(briefs, timeZone);

	if (nextBrief) {
		return <DashboardMeetingPrepLiveRow nextBrief={nextBrief} timeZone={timeZone} />;
	}

	if (todayDay) {
		return <DashboardMeetingPrepTodayDoneRow todayDay={todayDay} />;
	}

	const hasDelivered = hasEverDelivered(template);

	if (!template.scheduleEnabled) {
		return hasDelivered ? (
			<DashboardMeetingPrepPausedRow />
		) : (
			<DashboardMeetingPrepFirstRunRow template={template} timeZone={timeZone} />
		);
	}

	if (!hasDelivered) {
		return <DashboardMeetingPrepFirstRunRow template={template} timeZone={timeZone} />;
	}

	return <DashboardMeetingPrepQuietRow template={template} timeZone={timeZone} />;
};
